import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "./context/AuthContext.jsx";
import { useI18n } from "./i18n/I18nContext.jsx";
import { PageTitleProvider } from "./context/PageTitleContext.jsx";
import Layout from "./components/Layout.jsx";
import Login from "./pages/Login.jsx";
import ChangePassword from "./pages/ChangePassword.jsx";
import Dashboard from "./pages/Dashboard.jsx";
import ParaList from "./pages/ParaList.jsx";
import ParaDetail from "./pages/ParaDetail.jsx";
import OfficerSubmission from "./pages/OfficerSubmission.jsx";
import Officers from "./pages/Officers.jsx";
import OfficerProfile from "./pages/OfficerProfile.jsx";
import Zones from "./pages/Zones.jsx";
import ZoneProfile from "./pages/ZoneProfile.jsx";
import Notifications from "./pages/Notifications.jsx";
import ActivityLog from "./pages/ActivityLog.jsx";
import Reviews from "./pages/Reviews.jsx";
import Analytics from "./pages/Analytics.jsx";
import ClosedParaList from "./pages/ClosedParaList.jsx";
import Departments from "./pages/Departments.jsx";
import SubmittedAnswers from "./pages/SubmittedAnswers.jsx";
import AddUser from "./pages/AddUser.jsx";

export default function App() {
  const { user, loading } = useAuth();
  const { t } = useI18n();

  if (loading) {
    return <div className="center-screen">{t("loading")}</div>;
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Personal mobile login for ZONAL_AC / DEPT_HEAD acts as an Officer
  // and should have restricted access (no Officers/Zones/Analytics/etc).
  const isPersonalLogin =
    (user.role === "ZONAL_AC" && !user.is_zone_account) ||
    (user.role === "DEPT_HEAD" && !user.is_department_account);

  // CORP_ADMIN with a zone or department is scoped — hide zone/dept management pages.
  const isRestrictedCorpAdmin =
    user.role === "CORP_ADMIN" && (!!user.zone || !!user.department);

  const canSeeOfficers =
    !isPersonalLogin && (
      ["SUPER_ADMIN", "CORP_ADMIN", "AUDITOR"].includes(user.role) ||
      (user.role === "ZONAL_AC" && user.is_zone_account)
    );
  const canSeeZones = ["SUPER_ADMIN", "CORP_ADMIN", "AUDITOR"].includes(user.role) && !isPersonalLogin && !isRestrictedCorpAdmin;
  const canReview = ["AUDITOR", "SUPER_ADMIN"].includes(user.role) && !isPersonalLogin;
  const canSeeAnalytics = ["SUPER_ADMIN", "CORP_ADMIN", "AUDITOR"].includes(user.role) && !isPersonalLogin;
  const canManageDepartments = ["SUPER_ADMIN", "CORP_ADMIN"].includes(user.role) && !isPersonalLogin && !isRestrictedCorpAdmin;
  // Submitted Answers is for actual officers AND for personal-mobile logins
  // (ZONAL_AC / DEPT_HEAD acting as officers).
  const canSeeSubmittedAnswers = ["OFFICER", "DEPT_HEAD"].includes(user.role) || isPersonalLogin;
  // Add User page is available to all admin-type logins:
  //   - SUPER_ADMIN, CORP_ADMIN
  //   - Zone CUG accounts (ZONAL_AC + is_zone_account)
  //   - Department CUG accounts (DEPT_HEAD + is_department_account)
  const canAddUser =
    !isPersonalLogin && (
      ["SUPER_ADMIN", "CORP_ADMIN"].includes(user.role) ||
      (user.role === "ZONAL_AC" && user.is_zone_account) ||
      (user.role === "DEPT_HEAD" && user.is_department_account)
    );

  return (
    <PageTitleProvider>
      <Layout>
        <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/paras" element={<ParaList />} />
        <Route path="/paras/:id" element={<ParaDetail />} />
        <Route path="/closed-paras" element={<ClosedParaList />} />
        <Route path="/submit" element={<OfficerSubmission />} />
        
        {canSeeOfficers && <Route path="/officers" element={<Officers />} />}
        {canSeeOfficers && <Route path="/officers/:id" element={<OfficerProfile />} />}
        
        {canSeeZones && <Route path="/zones" element={<Zones />} />}
        {canSeeZones && <Route path="/zones/:id" element={<ZoneProfile />} />}
        
        {canManageDepartments && <Route path="/departments" element={<Departments />} />}
        
        {canReview && <Route path="/reviews" element={<Reviews />} />}
        {canSeeAnalytics && <Route path="/analytics" element={<Analytics />} />}
        {canSeeSubmittedAnswers && <Route path="/submitted-answers" element={<SubmittedAnswers />} />}
        {canAddUser && <Route path="/add-user" element={<AddUser />} />}
        
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/activity" element={<ActivityLog />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </PageTitleProvider>
  );
}
